import { useState, useEffect } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { adminApi } from '@/services/adminApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface School {
  schoolId: string;
  name: string;
  schoolCode: string;
  district: string;
  state: string;
  subscriptionTier: string;
  isActive: boolean;
  createdAt: string;
}

export function SchoolsPage() {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadSchools();
  }, [page]);

  const loadSchools = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getSchools({ page, limit });
      setSchools(data.schools);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const filteredSchools = schools.filter((school) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      school.name.toLowerCase().includes(search) ||
      school.district.toLowerCase().includes(search) ||
      school.state.toLowerCase().includes(search)
    );
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'trial':
        return 'warning';
      case 'expired':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Building2 className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-foreground">Schools</h1>
          <p className="text-muted-foreground mt-1">
            Manage school accounts and subscriptions
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, district, or state..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button variant="outline" onClick={() => loadSchools()}>
              <Filter className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Schools ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingSpinner message="Loading schools..." />
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadSchools}>Retry</Button>
            </div>
          ) : filteredSchools.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No schools found
            </p>
          ) : (
            <>
              <div className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>School</th>
                      <th>Code</th>
                      <th>District</th>
                      <th>State</th>
                      <th>Subscription</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSchools.map((school) => (
                      <tr key={school.schoolId}>
                        <td className="font-medium">{school.name}</td>
                        <td className="text-muted-foreground">{school.schoolCode || '-'}</td>
                        <td className="text-muted-foreground">{school.district || '-'}</td>
                        <td>{school.state || '-'}</td>
                        <td>
                          <Badge variant={getStatusBadgeVariant(school.subscriptionTier)}>
                            {school.subscriptionTier || 'None'}
                          </Badge>
                        </td>
                        <td>
                          <Badge variant={school.isActive ? 'success' : 'destructive'}>
                            {school.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button variant="ghost" size="sm">
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
